import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Package, Users, BarChart3, LogOut, Gem, Calculator, FileImage, Menu, X, ChevronDown, Table2, Factory, Sparkles, Globe, FileText, Key, MoreHorizontal } from 'lucide-react';
import { USER_ROLE_LABELS } from '@/utils/constants';
import { isStonesOnlyHost, siteModeLabel } from '@/utils/host';

// Top-level items — always visible in the bar.
const navItems = [
  { path: '/analytics', label: 'Analytics', icon: <BarChart3 size={16} />, roles: ['admin', 'special_executor'] },
  { path: '/tasks', label: 'Tasks', icon: <Package size={16} />, roles: ['admin', 'executor', 'special_executor', 'customer', 'final_executor'] },
  { path: '/all-tasks', label: 'All Tasks', icon: <Table2 size={16} />, roles: ['admin', 'executor', 'special_executor', 'customer', 'final_executor'] },
  { path: '/executor-tasks', label: 'Executors Task', icon: <Table2 size={16} />, roles: ['admin', 'special_executor'] },
  { path: '/stones', label: 'Stones', icon: <Gem size={16} />, roles: ['admin', 'stone_supplier'] },
  { path: '/quotation', label: 'Quotation', icon: <Calculator size={16} />, roles: ['admin'] },
];

// Lower-traffic items grouped under the "Others" dropdown to reduce nav clutter.
const otherNavItems = [
  { path: '/cad-sheets', label: 'CAD Sheets', icon: <FileImage size={16} />, roles: ['admin', 'executor', 'special_executor', 'final_executor'] },
  { path: '/portal-import', label: 'SGI Import', icon: <Globe size={16} />, roles: ['admin', 'executor', 'special_executor'] },
  { path: '/manufacturing', label: 'Manufacturing', icon: <Factory size={16} />, roles: ['admin'] },
  { path: '/pd-ai', label: 'PD AI', icon: <Sparkles size={16} />, roles: ['admin'] },
  { path: '/audit-log', label: 'Audit Log', icon: <FileText size={16} />, roles: ['admin'] },
  { path: '/users', label: 'Users', icon: <Users size={16} />, roles: ['admin'] },
  { path: '/admin/llm-config', label: 'LLM Config', icon: <Key size={16} />, roles: ['admin'] },
];

const CollapsibleLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [othersMenuOpen, setOthersMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const othersMenuRef = useRef(null);

  const userRole = user?.role || 'executor';
  const roleLabel = USER_ROLE_LABELS[userRole] || userRole;
  const stonesOnly = isStonesOnlyHost();
  const brandLabel = siteModeLabel();

  const roleVisible = (item) => {
    if (stonesOnly && item.path !== '/stones') return false;
    if (!item.roles) return true;
    return item.roles.includes(userRole);
  };

  const visibleItems = navItems.filter(roleVisible);
  const visibleOthers = otherNavItems.filter(roleVisible);
  const othersActive = visibleOthers.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
      if (othersMenuRef.current && !othersMenuRef.current.contains(e.target)) setOthersMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      {/* Top Navigation Bar */}
      <nav className="sticky top-0 z-50 border-b border-border bg-navy-deep text-white" data-testid="top-navbar">
        <div className="flex items-center justify-between h-12 px-4">
          {/* Left: Logo + Nav */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(stonesOnly ? '/stones' : '/tasks')}>
              <Gem size={20} className="text-gold" />
              <span className="font-bold text-sm tracking-wide hidden sm:inline">{brandLabel}</span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {visibleItems.map(item => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-base ease-out-expo ${
                      isActive
                        ? 'bg-white/15 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                );
              })}

              {/* Others dropdown — groups low-traffic admin pages */}
              {visibleOthers.length > 0 && (
                <div className="relative" ref={othersMenuRef}>
                  <button
                    onClick={() => setOthersMenuOpen(!othersMenuOpen)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-base ease-out-expo ${
                      othersActive
                        ? 'bg-white/15 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="nav-others"
                    aria-haspopup="menu"
                    aria-expanded={othersMenuOpen}
                  >
                    <MoreHorizontal size={16} />
                    Others
                    <ChevronDown size={12} className={`transition-transform duration-base ease-out-expo ${othersMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {othersMenuOpen && (
                    <div className="motion-fade absolute left-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                      {visibleOthers.map(item => {
                        const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                        return (
                          <button
                            key={item.path}
                            onClick={() => { navigate(item.path); setOthersMenuOpen(false); }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors duration-base ease-out-expo ${
                              isActive
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'text-foreground hover:bg-muted/50'
                            }`}
                            data-testid={`nav-other-${item.path.replace(/\//g, '-')}`}
                          >
                            <span className="text-muted-foreground">{item.icon}</span>
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: User Info */}
          <div className="flex items-center gap-3">
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 text-xs text-white/80 hover:text-white transition-colors duration-base ease-out-expo"
                data-testid="user-menu-trigger"
              >
                <div className="w-7 h-7 rounded-full bg-gold/20 border border-gold/50 flex items-center justify-center text-gold text-[11px] font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-xs font-medium leading-tight">{user?.name}</div>
                  <div className="text-[10px] text-white/50">{roleLabel}</div>
                </div>
                <ChevronDown size={14} className="text-white/40" />
              </button>

              {userMenuOpen && (
                <div className="motion-fade absolute right-0 top-full mt-1 w-44 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-xs font-medium text-foreground truncate">{user?.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
                    <span className="inline-block mt-1 px-1.5 py-0.5 text-[9px] rounded bg-primary/10 text-primary font-medium">{roleLabel}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-destructive hover:bg-muted/50 transition-colors duration-base ease-out-expo"
                    data-testid="logout-btn"
                  >
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-1.5 rounded-md hover:bg-white/10 transition-colors duration-base ease-out-expo"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-btn"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Dropdown — flat list, no Others nesting (already a vertical menu) */}
        {menuOpen && (
          <div className="md:hidden border-t border-white/10 px-4 py-2 space-y-1">
            {[...visibleItems, ...visibleOthers].map(item => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setMenuOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors duration-base ease-out-expo ${
                    isActive ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
};

export default CollapsibleLayout;
